import { initializePaddle, type Paddle, type Environments } from '@paddle/paddle-js'

/** Paddle price IDs — overridable via env, defaulting to production catalog. */
export const PADDLE_PRICES: Record<string, string> = {
  '1month': import.meta.env.VITE_PADDLE_PRICE_1MONTH || 'pri_01kh214rfatapqy6bq5xv1rfk9',
  '3month': import.meta.env.VITE_PADDLE_PRICE_3MONTH || 'pri_01kh215rrwbavhx3dhwt3sbx6p',
  '1year': import.meta.env.VITE_PADDLE_PRICE_1YEAR || 'pri_01kh2178g2rhmjfktdbj8emqyf',
}

export function isPaddleConfigured(): boolean {
  return !!import.meta.env.VITE_PADDLE_CLIENT_TOKEN
}

// A single module-level handler the Gate registers; invoked on checkout.completed.
let completeHandler: ((data: unknown) => void) | null = null
export function setCheckoutCompleteHandler(fn: ((data: unknown) => void) | null) {
  completeHandler = fn
}

let paddlePromise: Promise<Paddle | undefined> | null = null

export function getPaddle(): Promise<Paddle | undefined> {
  if (!paddlePromise) {
    const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN
    if (!token) return Promise.resolve(undefined)
    const environment = (import.meta.env.VITE_PADDLE_ENVIRONMENT || 'production') as Environments
    paddlePromise = initializePaddle({
      token,
      environment,
      eventCallback: (event) => {
        if (event?.name === 'checkout.completed') completeHandler?.(event.data)
      },
    })
  }
  return paddlePromise
}

/**
 * Live localized prices from Paddle for the catalog price IDs, keyed by plan
 * key ('1month' | '3month' | '1year'). Returns null when Paddle is not
 * configured or the preview fails — callers keep their fallback strings.
 */
export async function fetchPlanPrices(): Promise<Record<string, string> | null> {
  const paddle = await getPaddle()
  if (!paddle) return null
  try {
    const preview = await paddle.PricePreview({
      items: Object.values(PADDLE_PRICES).map((priceId) => ({ priceId, quantity: 1 })),
    })
    const out: Record<string, string> = {}
    for (const item of preview.data.details.lineItems) {
      const key = Object.keys(PADDLE_PRICES).find((k) => PADDLE_PRICES[k] === item.price.id)
      if (key) out[key] = item.formattedTotals.total
    }
    return Object.keys(out).length ? out : null
  } catch {
    return null
  }
}

export async function openCheckout(opts: { priceId: string; email?: string }): Promise<void> {
  const paddle = await getPaddle()
  if (!paddle) throw new Error('paddle-not-configured')
  paddle.Checkout.open({
    items: [{ priceId: opts.priceId, quantity: 1 }],
    customer: opts.email ? { email: opts.email } : undefined,
    settings: { displayMode: 'overlay' },
  })
}
